interface AvatarCache {
  [key: string]: {
    url: string;
    timestamp: number;
  };
}

class AvatarCacheManager {
  private cache: AvatarCache = {};
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
  private readonly STORAGE_KEY = 'avatar_cache';

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        this.cache = JSON.parse(stored);
        this.cleanupExpired();
      }
    } catch (error) {
      console.warn('Failed to load avatar cache from storage:', error);
    }
  }

  private saveToStorage(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.cache));
    } catch (error) {
      console.warn('Failed to save avatar cache to storage:', error);
    }
  }

  private cleanupExpired(): void {
    const now = Date.now();
    Object.keys(this.cache).forEach(key => {
      if (now - this.cache[key].timestamp > this.CACHE_DURATION) {
        delete this.cache[key];
      }
    });
  }

  public getAvatarUrl(name: string, background: string = '007E39', color: string = 'fff', size: number = 100): string {
    const cacheKey = `${name}_${background}_${color}_${size}`;
    
    // Check cache first
    if (this.cache[cacheKey]) {
      const cached = this.cache[cacheKey];
      if (Date.now() - cached.timestamp < this.CACHE_DURATION) {
        return cached.url;
      } else {
        delete this.cache[cacheKey];
      }
    }

    // Generate new URL with optimized size
    const url = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=${background}&color=${color}&size=${size}&format=png`;
    
    // Cache it
    this.cache[cacheKey] = {
      url,
      timestamp: Date.now()
    };
    
    this.saveToStorage();
    return url;
  }

  public preloadAvatar(name: string, background: string = '007E39', color: string = 'fff', size: number = 100): void {
    const url = this.getAvatarUrl(name, background, color, size);
    
    // Preload image
    const img = new Image();
    img.src = url;
  }

  public clearCache(): void {
    this.cache = {};
    localStorage.removeItem(this.STORAGE_KEY);
  }
}

export const avatarCache = new AvatarCacheManager();
